import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import {
  injectIntl,
  intlShape,
  defineMessages,
  FormattedMessage,
} from 'react-intl';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { convertFromRaw, EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import _ from 'lodash';
import moment from 'moment';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableFooter from '@material-ui/core/TableFooter';

import { Interweave } from 'interweave';
import * as authActions from '../../actions/auth';
import * as discussionMessagesActions from '../../actions/discussionMessages';
import * as editDiscussionActions from '../../actions/editDiscussion';
import { isAdmin, isTeacher, isGroupLeader } from '../../helpers';
import TablePaginationActions from './TablePaginationActions';
import PostDiscussionMessageForm from './PostDiscussionMessageForm/PostDiscussionMessageForm';

import {
  ResponsiveTableBody,
  TablePagination,
} from '../statelessComponents/Table';
import ContentContainer from '../statelessComponents/ScreenParts/ContentContainer';
import CircularProgressLoader from '../CircularProgressLoader/CircularProgressLoader';
import Link from '../Link/Link';
import UserAvatar from '../UserAvatar/UserAvatar';

import s from './Discussion.scss';
import SkholeIcon13 from '../statelessComponents/Icons/SkholeIcon13';
import SkholeIcon47 from '../statelessComponents/Icons/SkholeIcon47';
import SkholeIcon48 from '../statelessComponents/Icons/SkholeIcon48';

const messages = defineMessages({
  followDiscussionTooltip: {
    id: 'Discussions.followDiscussionTooltip',
    defaultMessage: 'Follow discussion to get notifications',
    description:
      'Tooltip over follow discussion button in the first discussion post',
  },
  unfollowDiscussionTooltip: {
    id: 'Discussions.unfollowDiscussionTooltip',
    defaultMessage: 'Unfollow discussion',
    description:
      'Tooltip over unfollow discussion button in the first discussion post',
  },
  deleteMessageConfirmationText: {
    id: 'Discussions.deleteMessageConfirmationText',
    defaultMessage: 'Do you really want to delete message?',
    description: 'The text displating in delete message confirm dialog',
  },
  deleteDiscussionConfirmationText: {
    id: 'Discussions.deleteDiscussionConfirmationText',
    defaultMessage: 'Do you really want to delete discussion?',
    description: 'The text displating in delete discussion confirm dialog',
  },
  cancelRemoveLabel: {
    id: 'General.refuseDeleteBtnLabel',
    defaultMessage: 'Oops! no thanks',
  },
  deleteBtnLabel: {
    id: 'General.deleteBtnLabel',
    defaultMessage: 'Delete',
  },
});

@injectIntl
@withStyles(s)
@connect(
  state => ({
    auth: state.auth,
    followedDiscussions: state.auth.followedDiscussions,
    discussion: state.editDiscussion.discussion,
    error: state.editDiscussion.error,
    loading: state.discussionMessages.loading,
    loaded: state.discussionMessages.loaded,
    discussionMessages: state.discussionMessages.messages,
    total: state.discussionMessages.total,
    lang: state.intl.locale,
  }),
  dispatch =>
    bindActionCreators(
      {
        ...discussionMessagesActions,
        ...editDiscussionActions,
        saveFollowedDiscussions: authActions.saveFollowedDiscussions,
      },
      dispatch,
    ),
)
@connect()
export default class Discussion extends React.Component {
  static propTypes = {
    error: PropTypes.object, // eslint-disable-line
    auth: PropTypes.object, // eslint-disable-line
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    discussion: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    discussionMessages: PropTypes.array.isRequired, // eslint-disable-line
    getMessages: PropTypes.func.isRequired,
    resetDiscussion: PropTypes.func.isRequired,
    resetMessages: PropTypes.func.isRequired,
    saveFollowedDiscussions: PropTypes.func.isRequired,
    followedDiscussions: PropTypes.arrayOf(PropTypes.string.isRequired)
      .isRequired,
    intl: intlShape.isRequired,
    ts: PropTypes.string,
    lang: PropTypes.string.isRequired,
    dispatch: PropTypes.func, // eslint-disable-line
  };

  static defaultProps = {
    ts: Date.now(),
  };

  static contextTypes = {
    fetch: PropTypes.func,
  };

  state = {
    perPage: 50,
    page: 0,
    messagesEditor: [],
    isDiscussionFollowing: false,
    dialogMessage: messages.deleteMessageConfirmationText,
    objToDelete: null,
    isDeleting: false,
    isConfirmDeleteOpen: false,
  };

  componentDidMount() {
    this.loadMessages();
  }

  componentWillReceiveProps(nextProps) {
    if (
      _.get(this, 'props.discussionMessages') !==
      _.get(nextProps, 'discussionMessages')
    ) {
      this.setState({
        messagesEditor: _.map(nextProps.discussionMessages, ({ text }) =>
          EditorState.createWithContent(convertFromRaw(text)),
        ),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      _.get(this, 'props.discussion._id') !==
        _.get(prevProps, 'discussion._id') ||
      _.get(this, 'props.ts') !== _.get(prevProps, 'ts')
    ) {
      this.props.resetMessages();
      this.clearAndLoadMessages();
    }

    if (prevProps.loading && !this.props.loading && window !== undefined) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  componentWillUnmount() {
    this.props.resetDiscussion();
    this.props.resetMessages();
  }

  onPostMessage = async () => {
    const { discussion, dispatch } = this.props;
    const { fetch } = this.context;
    const { getMessages } = discussionMessagesActions;
    const { perPage, page } = this.state;

    const discussionId = _.get(discussion, '_id');

    const getMessagesAction = getMessages(discussionId, {
      page: page + 1,
      perPage,
    });

    await dispatch({
      type: getMessagesAction.types[0],
    });

    try {
      const res = await getMessagesAction.promise(fetch);

      const resText = await res.text();

      const json = resText ? JSON.parse(resText) : {};

      if (res.status !== 200) {
        if ([403, 404].includes(res.status)) {
          console.error('MIDDLEWARE ERROR:', res.status, json);
          return;
        }

        await dispatch({
          error: json,
          type: getMessagesAction.types[2],
        });
      } else {
        const result = {
          status: res.status,
          json,
          total: Number.parseInt(res.headers.get('x-total-count'), 10),
        };

        await dispatch({
          result,
          type: getMessagesAction.types[1],
        });

        this.handleChangePage(
          null,
          Math.max(0, Math.ceil(this.props.total / perPage)) - 1,
        );
      }
    } catch (error) {
      console.error('MIDDLEWARE ERROR:', error);
      await dispatch({
        error,
        type: getMessagesAction.types[2],
      });
    }
  };

  onDeleteConfirm = async () => {
    this.setState({ isDeleting: true });
    const { name } = this.state.objToDelete;
    if (name) {
      this.removeDiscussion();
    } else {
      this.removeMessage(this.state.objToDelete);
    }
  };

  askConfirmToDelete = message => {
    const { name } = message;
    this.setState(
      {
        objToDelete: message,
        dialogMessage: name
          ? messages.deleteDiscussionConfirmationText
          : messages.deleteMessageConfirmationText,
      },
      this.toggleDialog,
    );
  };

  removeMessage = ({ _id }) => {
    const discussionId = _.get(this.props.discussion, '_id');
    return this.context
      .fetch(`/api/discussions/${discussionId}/messages/${_id}`, {
        method: 'DELETE',
      })
      .then(() => {
        this.setState({ isDeleting: false });
        this.toggleDialog();
        this.clearAndLoadMessages();
      })
      .catch(err => console.error(err));
  };

  removeDiscussion = () => {
    const discussionId = _.get(this.props.discussion, '_id');
    return this.context
      .fetch(`/api/discussions/${discussionId}`, {
        method: 'DELETE',
      })
      .then(() => {
        this.toggleDialog();
        this.goDiscussionsPage();
      })
      .catch(err => console.error(err));
  };

  goDiscussionsPage = () => {
    history.back(); // eslint-disable-line
  };

  toggleDialog = () => {
    this.setState(prevState => ({
      isConfirmDeleteOpen: !prevState.isConfirmDeleteOpen,
    }));
  };

  toggleFollowDiscussion = _id => {
    const { followedDiscussions, saveFollowedDiscussions } = this.props;

    if (followedDiscussions.includes(_id)) {
      return this.context
        .fetch(`/api/auth/followed-discussions/${_id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': undefined,
            Accept: '*/*',
          },
        })
        .then(() =>
          saveFollowedDiscussions(_.without(followedDiscussions, _id)),
        )
        .then(() => {
          this.setState({
            isDiscussionFollowing: false,
          });
        })
        .catch(error => {
          console.error(error);
          this.setState({
            isDiscussionFollowing: false,
          });
        });
    }

    this.setState({
      isDiscussionFollowing: true,
    });

    return this.context
      .fetch(`/api/auth/followed-discussions/${_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': undefined,
          Accept: '*/*',
        },
      })
      .then(() => saveFollowedDiscussions(followedDiscussions.concat([_id])))
      .then(() => {
        this.setState({
          isDiscussionFollowing: false,
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({
          isDiscussionFollowing: false,
        });
      });
  };

  handleChangePage = (event, page) => {
    this.setState({ page }, this.loadMessages);
  };

  loadMessages = () => {
    const { getMessages, discussion } = this.props;
    const { perPage, page } = this.state;

    const discussionId = _.get(discussion, '_id');

    return getMessages(discussionId, {
      page: page + 1,
      perPage,
    });
  };

  clearAndLoadMessages = () => {
    this.setState(
      {
        page: 0,
      },
      this.loadMessages,
    );
  };

  render() {
    const {
      error,
      loading,
      loaded,
      discussionMessages,
      total,
      discussion,
      auth,
      followedDiscussions,
      intl: { formatMessage },
    } = this.props;

    const {
      perPage,
      page,
      messagesEditor,
      isDeleting,
      isDiscussionFollowing,
      dialogMessage,
      isConfirmDeleteOpen,
    } = this.state;

    const discussionId = _.get(discussion, '_id');

    const isFollowed = (followedDiscussions || []).includes(discussion._id);

    const userCanDelete =
      isAdmin(auth) || isTeacher(auth) || isGroupLeader(auth);

    if (error) {
      return (
        <div className={s.root}>
          <div className={s.container}>
            <Typography gutterBottom color="error">
              {error.error}
            </Typography>
          </div>
        </div>
      );
    }

    const pagination = withBorder => (
      <TableRow>
        <TablePagination
          colSpan={6}
          count={total}
          rowsPerPage={perPage}
          page={page}
          withBorder={withBorder}
          backIconButtonProps={{
            classes: {
              root: s.pagination,
            },
          }}
          nextIconButtonProps={{
            classes: {
              root: s.pagination,
            },
          }}
          rowsPerPageOptions={[perPage]}
          onChangePage={this.handleChangePage}
          classes={{
            caption: s.pagination,
          }}
          ActionsComponent={TablePaginationActions}
        />
      </TableRow>
    );

    return (
      <ContentContainer>
        <div className={s.header}>
          <IconButton component={Link} to="./" className={s.backButton}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="h2"
            className={s.title}
            data-test="discussion-header"
          >
            {_.get(discussion, 'name', '')}
          </Typography>
        </div>
        <div className={s.tableWrapper}>
          {loading && (
            <CircularProgressLoader rootStyle={{ position: 'absolute' }} />
          )}
          <Table className={s.table}>
            <TableHead>{pagination(true)}</TableHead>
            <ResponsiveTableBody
              isEmpty={loaded && _.isEmpty(discussionMessages)}
            >
              {discussionMessages.map((n, index) => (
                <TableRow key={n._id}>
                  <TableCell
                    className={classNames(s.tableCell, s.onlyMobileDisplayFlex)}
                    size="small"
                  >
                    <div className={s.flexRowAuthor}>
                      {n.createdBy && (
                        <UserAvatar user={n.createdBy} className={s.avatar} />
                      )}
                      <div className={s.flexColumn}>
                        <Typography
                          className={s.messageAuthor}
                          component={Link}
                          to={`/profiles/${_.get(n, 'createdBy._id')}`}
                        >
                          {_.get(n, 'createdBy.lastName', '')}&nbsp;
                          {_.get(n, 'createdBy.firstName', '')}
                        </Typography>
                        <Typography className={s.messageDate}>
                          {moment(n.createDate).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </div>
                      {!!n.name && ( // dummy checking on discussion or discussion message item
                        <Tooltip
                          title={formatMessage(
                            isFollowed
                              ? messages.unfollowDiscussionTooltip
                              : messages.followDiscussionTooltip,
                          )}
                        >
                          <IconButton
                            disabled={isDiscussionFollowing}
                            onClick={() => this.toggleFollowDiscussion(n._id)}
                            classes={{
                              root: s.bellIconRoot,
                            }}
                          >
                            {/* {isFollowed ? (
                              <div className={s.notificationsOffIcon} />
                            ) : (
                              <div className={s.notificationsIcon} />
                            )} */}
                            {isFollowed ? (
                              <SkholeIcon48
                                className={s.notificationsOffIcon}
                              />
                            ) : (
                              <SkholeIcon47 className={s.notificationsIcon} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <Typography className={s.message} component="div">
                      <Interweave
                        noWrap
                        content={
                          messagesEditor[index]
                            ? `${stateToHTML(
                                messagesEditor[index].getCurrentContent(),
                              )}`
                            : ''
                        }
                      />
                    </Typography>
                    <div className={s.flex} />
                  </TableCell>
                  <TableCell
                    className={classNames(
                      s.tableCell,
                      s.avatarCell,
                      s.notDisplayOnMobile,
                    )}
                    size="small"
                  >
                    {n.createdBy && (
                      <UserAvatar user={n.createdBy} className={s.avatar} />
                    )}
                    <Typography
                      className={s.messageAuthor}
                      component={Link}
                      to={`/profiles/${_.get(n, 'createdBy._id')}`}
                    >
                      {_.get(n, 'createdBy.lastName', '')}&nbsp;
                      {_.get(n, 'createdBy.firstName', '')}
                    </Typography>
                    <Typography className={s.messageDate}>
                      {moment(n.createDate).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                    {!!n.name && ( // dummy checking on discussion or discussion message item
                      <Tooltip
                        title={formatMessage(
                          isFollowed
                            ? messages.unfollowDiscussionTooltip
                            : messages.followDiscussionTooltip,
                        )}
                      >
                        <IconButton
                          disabled={isDiscussionFollowing}
                          onClick={() => this.toggleFollowDiscussion(n._id)}
                        >
                          {isFollowed ? (
                            <div className={s.notificationsOffIcon} />
                          ) : (
                            <div className={s.notificationsIcon} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell
                    className={classNames(s.tableCell, s.notDisplayOnMobile)}
                  >
                    <Typography className={s.message} component="div">
                      <Interweave
                        noWrap
                        content={
                          messagesEditor[index]
                            ? `${stateToHTML(
                                messagesEditor[index].getCurrentContent(),
                              )}`
                            : ''
                        }
                      />
                    </Typography>
                    <div className={s.flex} />
                  </TableCell>
                  {userCanDelete && (
                    <TableCell className={classNames(s.removeCell)}>
                      <IconButton
                        disabled={isDiscussionFollowing}
                        onClick={() => this.askConfirmToDelete(n)}
                      >
                        <SkholeIcon13 className={s.removeIcon} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </ResponsiveTableBody>
            <TableFooter>
              {pagination()}
              <TableRow>
                <TableCell
                  className={classNames(
                    s.tableCell,
                    s.noBorderCell,
                    s.avatarCell,
                  )}
                >
                  {auth.user && (
                    <UserAvatar user={auth.user} className={s.avatar} />
                  )}
                  <Typography
                    className={s.messageAuthor}
                    component={Link}
                    to={`/profiles/${_.get(auth, 'user._id')}`}
                  >
                    {_.get(auth, 'user.lastName', '')}&nbsp;
                    {_.get(auth, 'user.firstName', '')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={classNames(s.tableCell, s.noBorderCell)}
                  colSpan={8}
                >
                  <PostDiscussionMessageForm
                    discussionId={discussionId}
                    onSave={this.onPostMessage}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
        <Dialog
          open={isConfirmDeleteOpen}
          onClose={this.toggleDialog}
          aria-describedby="remove-dialog-description"
          classes={{
            paperWidthSm: s.confirmDeleteDialog, // s.dialog,
          }}
        >
          <DialogTitle
            id="remove-dialog-title"
            classes={{
              root: s.confirmDeleteText,
              title: s.confirmDeleteText,
            }}
          >
            <FormattedMessage {...dialogMessage} />
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={this.toggleDialog}
              color="primary"
              disabled={isDeleting}
            >
              <FormattedMessage {...messages.cancelRemoveLabel} />
            </Button>
            <Button
              onClick={this.onDeleteConfirm}
              color="primary"
              autoFocus
              disabled={isDeleting}
            >
              {isDeleting && (
                <CircularProgress
                  className={s.delFabProgress}
                  size={25}
                  rootStyle={{ color: 'green' }}
                />
              )}
              <FormattedMessage {...messages.deleteBtnLabel} />
            </Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    );
  }
}
